/**
 * Settings pop-up (start screen only). Audio (mute + music/SFX volume), haptics, handedness,
 * how-to-play and credits. Pure DOM/CSS over §8 tokens; it holds no state of its own — it reads the
 * current {@link Settings} via `deps.get()` when opened and reports edits via `deps.onChange(patch)`
 * (which main.ts applies to the audio engine + renderer and persists).
 */
import type { Settings } from '../app/settings.ts';

export interface SettingsPanelDeps {
  /** Current settings (read when the panel opens to reflect persisted state). */
  get(): Settings;
  /** Apply + persist a change. */
  onChange(patch: Partial<Settings>): void;
  /** Replay the interactive tutorial (host closes the panel + starts it). */
  onReplayTutorial(): void;
}

export interface SettingsPanel {
  open(): void;
  close(): void;
  readonly el: HTMLElement;
}

const STYLE_ID = 'fc-settings-style';
const CSS = `
.fc-set-backdrop{position:absolute;inset:0;z-index:60;display:none;align-items:center;justify-content:center;
  background:rgba(6,12,18,.66);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);
  padding:calc(16px + env(safe-area-inset-top)) 16px calc(16px + env(safe-area-inset-bottom));}
.fc-set-backdrop.is-open{display:flex;}
.fc-set-card{width:min(420px,92vw);max-height:88%;overflow-y:auto;background:var(--dusk-2,#1a3145);
  border:1px solid #2c4255;border-radius:16px;box-shadow:0 16px 48px rgba(0,0,0,.5);color:var(--cream,#f4efe2);
  font-family:var(--font-body,system-ui,sans-serif);padding:18px 18px 22px;}
.fc-set-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;}
.fc-set-title{font-family:var(--font-pixel,monospace);font-size:13px;color:var(--gold,#f2c14e);letter-spacing:0;}
.fc-set-x{appearance:none;border:1px solid #2c4255;background:rgba(8,16,22,.6);color:var(--cream,#f4efe2);
  width:30px;height:30px;border-radius:8px;font-size:16px;line-height:1;cursor:pointer;}
.fc-set-sec{margin-top:16px;}
.fc-set-sec__h{font-family:var(--font-pixel,monospace);font-size:8px;letter-spacing:.04em;text-transform:uppercase;
  opacity:.6;margin-bottom:8px;}
.fc-set-row{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:7px 0;}
.fc-set-row__l{font-size:13px;}
.fc-set-range{flex:0 0 150px;accent-color:var(--gold,#f2c14e);}
.fc-set-toggle{appearance:none;border:1px solid #2c4255;background:#15222d;color:var(--cream,#f4efe2);
  border-radius:999px;padding:5px 12px;font-size:12px;font-weight:600;cursor:pointer;min-width:64px;}
.fc-set-toggle[aria-pressed="true"]{background:var(--gold,#f2c14e);color:#1a1205;border-color:var(--gold,#f2c14e);}
.fc-set-seg{display:flex;border:1px solid #2c4255;border-radius:999px;overflow:hidden;}
.fc-set-seg button{appearance:none;border:0;background:#15222d;color:var(--cream,#f4efe2);padding:6px 14px;
  font-size:12px;font-weight:600;cursor:pointer;}
.fc-set-seg button.is-on{background:var(--gold,#f2c14e);color:#1a1205;}
.fc-set-help{font-size:12px;line-height:1.5;opacity:.85;}
.fc-set-credits{font-size:11px;opacity:.55;margin-top:4px;line-height:1.5;}
`;

function ensureStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

/** A labelled row holding a control on the right. */
function row(label: string, control: HTMLElement): HTMLElement {
  const r = document.createElement('div');
  r.className = 'fc-set-row';
  const l = document.createElement('span');
  l.className = 'fc-set-row__l';
  l.textContent = label;
  r.append(l, control);
  return r;
}

function section(title: string, ...rows: HTMLElement[]): HTMLElement {
  const sec = document.createElement('div');
  sec.className = 'fc-set-sec';
  const h = document.createElement('div');
  h.className = 'fc-set-sec__h';
  h.textContent = title;
  sec.append(h, ...rows);
  return sec;
}

export function createSettingsPanel(root: HTMLElement, deps: SettingsPanelDeps): SettingsPanel {
  ensureStyle();

  const backdrop = document.createElement('div');
  backdrop.className = 'fc-set-backdrop';
  const card = document.createElement('div');
  card.className = 'fc-set-card';
  backdrop.appendChild(card);

  // --- header ---
  const head = document.createElement('div');
  head.className = 'fc-set-head';
  const title = document.createElement('div');
  title.className = 'fc-set-title';
  title.textContent = 'Settings';
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'fc-set-x';
  close.setAttribute('aria-label', 'Close settings');
  close.textContent = '✕';
  head.append(title, close);

  // --- audio: mute toggle + two sliders ---
  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.className = 'fc-set-toggle';
  const musicRange = document.createElement('input');
  musicRange.type = 'range';
  musicRange.min = '0';
  musicRange.max = '100';
  musicRange.className = 'fc-set-range';
  const sfxRange = document.createElement('input');
  sfxRange.type = 'range';
  sfxRange.min = '0';
  sfxRange.max = '100';
  sfxRange.className = 'fc-set-range';

  // --- gameplay: handedness segmented + haptics toggle ---
  const handSeg = document.createElement('div');
  handSeg.className = 'fc-set-seg';
  const rightBtn = document.createElement('button');
  rightBtn.type = 'button';
  rightBtn.textContent = 'Right';
  const leftBtn = document.createElement('button');
  leftBtn.type = 'button';
  leftBtn.textContent = 'Left';
  handSeg.append(rightBtn, leftBtn);

  const hapticBtn = document.createElement('button');
  hapticBtn.type = 'button';
  hapticBtn.className = 'fc-set-toggle';

  // --- how to play + credits ---
  const help = document.createElement('p');
  help.className = 'fc-set-help';
  help.textContent =
    'Build a deck, then duel: pick a club, drag to aim, release to start the swing, and tap POWER then IMPACT. Read the green break — the ball rolls downhill after it lands. Lowest score when the clock runs out wins; a tie goes to a closest-to-the-pin playoff.';
  const replayBtn = document.createElement('button');
  replayBtn.type = 'button';
  replayBtn.className = 'fc-set-toggle';
  replayBtn.textContent = 'Replay tutorial';
  replayBtn.style.marginTop = '10px';
  replayBtn.addEventListener('click', () => deps.onReplayTutorial());
  const credits = document.createElement('p');
  credits.className = 'fc-set-credits';
  credits.textContent = 'Fairway Clash · v0.1 — a real-time golf duel.';

  card.append(
    head,
    section('Audio', row('Mute all', muteBtn), row('Music volume', musicRange), row('SFX volume', sfxRange)),
    section('Gameplay', row('Your golfer', handSeg), row('Haptics', hapticBtn)),
    section('How to play', help, replayBtn),
    section('About', credits),
  );
  root.appendChild(backdrop);

  const setToggleLabel = (btn: HTMLButtonElement, on: boolean, onText: string, offText: string): void => {
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    btn.textContent = on ? onText : offText;
  };

  /** Reflect the current persisted settings into the controls. */
  function sync(): void {
    const s = deps.get();
    setToggleLabel(muteBtn, s.muted, 'Muted', 'On');
    musicRange.value = String(Math.round(s.musicVol * 100));
    sfxRange.value = String(Math.round(s.sfxVol * 100));
    rightBtn.classList.toggle('is-on', s.handedness === 'right');
    leftBtn.classList.toggle('is-on', s.handedness === 'left');
    setToggleLabel(hapticBtn, s.haptics, 'On', 'Off');
  }

  muteBtn.addEventListener('click', () => {
    deps.onChange({ muted: !deps.get().muted });
    sync();
  });
  musicRange.addEventListener('input', () => deps.onChange({ musicVol: Number(musicRange.value) / 100 }));
  sfxRange.addEventListener('input', () => deps.onChange({ sfxVol: Number(sfxRange.value) / 100 }));
  rightBtn.addEventListener('click', () => {
    deps.onChange({ handedness: 'right' });
    sync();
  });
  leftBtn.addEventListener('click', () => {
    deps.onChange({ handedness: 'left' });
    sync();
  });
  hapticBtn.addEventListener('click', () => {
    deps.onChange({ haptics: !deps.get().haptics });
    sync();
  });

  const closePanel = (): void => backdrop.classList.remove('is-open');
  close.addEventListener('click', closePanel);
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) closePanel();
  });

  return {
    el: backdrop,
    open(): void {
      sync();
      backdrop.classList.add('is-open');
    },
    close: closePanel,
  };
}
